import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import {
  clearReadNotifications,
  deleteNotification,
  fetchInbox,
  fetchUnreadHuddleComments,
  fetchMutedTaskIds,
  markAllNotificationsRead,
  markTaskHuddleCommentsRead,
  muteTask,
  setNotificationRead,
  unmuteTask,
} from './queries';

/**
 * Server state for the Inbox and mutes. The Inbox is polled on an interval
 * rather than subscribed: no Realtime (locked decision), and a five-minute
 * lag on a notification is invisible in practice. A Messages @ does not wait
 * for it: useMessageAlerts refreshes the Inbox as soon as a room's unread
 * count goes up (checked every 60s by the unread poll).
 */

export const notificationKeys = {
  all: ['notifications'] as const,
  inbox: () => [...notificationKeys.all, 'inbox'] as const,
  huddleComments: () => [...notificationKeys.all, 'huddle-comments'] as const,
  mutes: () => [...notificationKeys.all, 'mutes'] as const,
};

export const INBOX_REFETCH_INTERVAL_MS = 300_000;

/** The signed-in user's id, or an empty string when access is not approved. */
function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useInbox() {
  const { client } = useAuth();
  return useQuery({
    queryKey: notificationKeys.inbox(),
    queryFn: () => fetchInbox(client),
    refetchInterval: INBOX_REFETCH_INTERVAL_MS,
    // Hidden tabs do not poll (egress). The window-focus refetch brings it up
    // to date when the tab is shown again, and a room's unread count going up
    // refreshes it sooner (useMessageAlerts, 2a).
    refetchIntervalInBackground: false,
    retry: false,
  });
}

/** The My Day Huddle strip shares Inbox's five-minute refresh rhythm. */
export function useUnreadHuddleComments(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: notificationKeys.huddleComments(),
    queryFn: () => fetchUnreadHuddleComments(client),
    enabled,
    refetchInterval: INBOX_REFETCH_INTERVAL_MS,
    retry: false,
  });
}

/**
 * Opening a Huddle task means its comments have been seen. Remove them from
 * the My Day cache immediately, then refresh both My Day and Inbox from the
 * same database rows.
 */
export function useMarkTaskHuddleCommentsRead() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => markTaskHuddleCommentsRead(client, taskId),
    onMutate: async (taskId) => {
      const key = notificationKeys.huddleComments();
      await queryClient.cancelQueries({ queryKey: key });
      const previous =
        queryClient.getQueryData<Awaited<ReturnType<typeof fetchUnreadHuddleComments>>>(key);
      if (previous) {
        queryClient.setQueryData(
          key,
          previous.filter((row) => row.task_id !== taskId),
        );
      }
      return { previous };
    },
    onError: (_error, _taskId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationKeys.huddleComments(), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.huddleComments() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.inbox() });
    },
  });
}

export function useSetNotificationRead() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ notificationId, isRead }: { notificationId: string; isRead: boolean }) =>
      setNotificationRead(client, notificationId, isRead),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.inbox() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.huddleComments() });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(client),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.inbox() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.huddleComments() });
    },
  });
}

export function useDeleteNotification() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => deleteNotification(client, notificationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.inbox() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.huddleComments() });
    },
  });
}

export function useClearReadNotifications() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => clearReadNotifications(client),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.inbox() });
    },
  });
}

/** Loaded once per session; a task panel checks membership client-side. */
export function useMutedTaskIds() {
  const { client } = useAuth();
  return useQuery({
    queryKey: notificationKeys.mutes(),
    queryFn: () => fetchMutedTaskIds(client),
    retry: false,
  });
}

export function useMuteTask() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const userId = useActorId();
  return useMutation({
    mutationFn: (taskId: string) => muteTask(client, { taskId, userId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.mutes() });
    },
  });
}

export function useUnmuteTask() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => unmuteTask(client, taskId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.mutes() });
    },
  });
}
